import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Checkout = () => {
  const { cartItems, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    // Simulate payment processing
    setTimeout(() => {
      // Store order details
      const orderDetails = {
        orderId: `ORD-${Date.now()}`,
        items: cartItems,
        total: getCartTotal(),
        customerInfo: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          company: formData.company,
        },
        orderDate: new Date().toISOString(),
      };

      localStorage.setItem('lastOrder', JSON.stringify(orderDetails));
      
      // Clear cart
      clearCart();
      
      toast.success('Order placed successfully!');
      setIsProcessing(false);
      
      // Redirect to confirmation page
      navigate('/order-confirmation');
    }, 2000);
  };

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="bg-white pt-16 min-h-screen">
      {/* Hero Section */}
      <section className="py-12 px-4 lg:px-8 bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a] text-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Secure Checkout</h1>
            <p className="text-xl text-white/80 flex items-center gap-2">
              <Lock size={20} /> Your information is safe and secure
            </p>
          </motion.div>
        </div>
      </section>

      {/* Checkout Form */}
      <section className="py-16 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Form Fields */}
              <div className="lg:col-span-2 space-y-8">
                {/* Contact Information */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-grey rounded-2xl p-8"
                >
                  <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                        placeholder="Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                        placeholder="Your Company"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Billing Address */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white border border-grey rounded-2xl p-8"
                >
                  <h2 className="text-2xl font-bold mb-6">Billing Address</h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Street Address *</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                        placeholder="123 Main St"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-semibold mb-2">City *</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                          placeholder="New York"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">State *</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                          placeholder="NY"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">ZIP Code *</label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                          placeholder="10001"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Payment Information */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white border border-grey rounded-2xl p-8"
                >
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                    <CreditCard size={28} />
                    Payment Information
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Card Number *</label>
                      <input
                        type="text"
                        name="cardNumber"
                        value={formData.cardNumber}
                        onChange={handleChange}
                        required
                        maxLength="19"
                        className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                        placeholder="1234 5678 9012 3456"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Cardholder Name *</label>
                      <input
                        type="text"
                        name="cardName"
                        value={formData.cardName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Expiry Date *</label>
                        <input
                          type="text"
                          name="expiryDate"
                          value={formData.expiryDate}
                          onChange={handleChange}
                          required
                          maxLength="5"
                          className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                          placeholder="MM/YY"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">CVV *</label>
                        <input
                          type="text"
                          name="cvv"
                          value={formData.cvv}
                          onChange={handleChange}
                          required
                          maxLength="4"
                          className="w-full px-4 py-3 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-light-pink"
                          placeholder="123"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-mid-grey mt-4 flex items-center gap-2">
                    <Lock size={16} />
                    Your payment information is encrypted and secure
                  </p>
                </motion.div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a] text-white rounded-2xl p-8 sticky top-24"
                >
                  <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
                  
                  <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                    {cartItems.map((item) => (
                      <div key={`${item.id}-${item.type}`} className="flex justify-between text-sm">
                        <div className="flex-1">
                          <p className="font-semibold">{item.name}</p>
                          <p className="text-white/60 text-xs">Qty: {item.quantity}</p>
                        </div>
                        <p className="font-semibold">{item.price}</p>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-white/20 pt-4 space-y-3">
                    <div className="flex justify-between text-lg">
                      <span className="text-white/80">Subtotal</span>
                      <span className="font-semibold">${getCartTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg">
                      <span className="text-white/80">Tax</span>
                      <span className="font-semibold">$0.00</span>
                    </div>
                    <div className="border-t border-white/20 pt-3">
                      <div className="flex justify-between text-2xl font-bold">
                        <span>Total</span>
                        <span className="bg-gradient-to-r from-light-pink to-mid-purple bg-clip-text text-transparent">
                          ${getCartTotal().toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full mt-8 bg-gradient-to-r from-light-pink to-mid-purple text-white py-4 rounded-full font-semibold hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isProcessing ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={20} />
                        Place Order
                      </>
                    )}
                  </button>

                  <p className="text-xs text-white/60 text-center mt-4">
                    By placing your order, you agree to our Terms & Conditions
                  </p>
                </motion.div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Checkout;
